import { IErrorApiResponse } from '@wyz/types';
import { REQUIRED_MESSAGE } from '@wyz/utils';
import { CommonErrorCodes } from '~/common/helpers';
import { AnyTranslationKeys, CommonErrorCodesEnumType } from '~/common/types';

function makeProxyHandler(fallback?: AnyTranslationKeys) {
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(target: object, propertyKey: PropertyKey, receiver?: any) {
      const value = Reflect.get(target, propertyKey, receiver);
      if (value === undefined) return fallback ?? propertyKey;
      return value;
    },
  };
}

export function createErrorLookup<TKeys extends string>(
  mapping: Record<TKeys, AnyTranslationKeys>,
  fallback?: AnyTranslationKeys,
): Record<TKeys, AnyTranslationKeys> {
  return new Proxy(mapping, makeProxyHandler(fallback)) as Record<
    TKeys,
    AnyTranslationKeys
  >;
}

export function extendErrorLookup<TBase extends string, TExtra extends string>(
  base: Record<TBase, AnyTranslationKeys>,
  extra: Record<TExtra, AnyTranslationKeys>,
  fallback?: AnyTranslationKeys,
): Record<TBase | TExtra, AnyTranslationKeys> {
  return new Proxy({ ...base, ...extra }, makeProxyHandler(fallback)) as Record<
    TBase | TExtra,
    AnyTranslationKeys
  >;
}

export const CommonErrorCodesLookup =
  createErrorLookup<CommonErrorCodesEnumType>({
    [CommonErrorCodes.ERR_VALUE_NOT_VALID_EMAIL]: 'app.common.email.invalid',
    [CommonErrorCodes.ERR_NOT_ALLOWED_GROUPS]:
      'app.user.validation.not_allowed_groups',
    [CommonErrorCodes.ERR_NO_ENTITIES_BOUND]:
      'app.user.validation.err_no_entities_bound',
    [CommonErrorCodes.ERR_CUSTOMER_OUT_OF_SCOPE]:
      'app.entity.detail.out_of_scope',
    [CommonErrorCodes.ERR_VALUE_SHOULD_NOT_BE_BLANK]: REQUIRED_MESSAGE,
    [CommonErrorCodes.ERR_UNIQUE]: 'app.common.validations.unique_value',
    [CommonErrorCodes.ERR_UNIQUE_CODE]: 'app.common.validations.code.unique',
    [CommonErrorCodes.ERR_DUPLICATE_VALUES]:
      'app.common.validations.common.duplicate_values',
    [CommonErrorCodes.ERR_UNIQUE_EMAIL_ADDRESS]:
      'app.common.validations.email.unique',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_POSITIVE]:
      'app.common.validations.positive_number',
    [CommonErrorCodes.ERR_VALIDATION]: 'app.common.validations.error_message',
    [CommonErrorCodes.ERR_UNIQUE_USER]: 'app.common.validations.unique_user',
    [CommonErrorCodes.ERR_UNIQUE_NAME]: 'app.common.validations.name.unique',
    [CommonErrorCodes.ERR_TOO_MANY_SUPPLIER_TEMPLATE_MATCHES]:
      'app.common.validations.supplier.template.overlapping',
    [CommonErrorCodes.ERR_TOO_MANY_REDISTRIBUTION_SUPPLIER_TEMPLATE_MATCHES]:
      'app.common.validations.redistribution_supplier.template.overlapping',
    [CommonErrorCodes.ERR_COUNTRY_IN_USE_BY_CUSTOMERS]:
      'app.common.validations.country.country_in_use_by_customers',
    [CommonErrorCodes.ERR_TOO_MANY_BRAND_TEMPLATE_MATCHES]:
      'app.common.validations.brand.template.overlapping',
    [CommonErrorCodes.ERR_TOO_MANY_SETTING_TEMPLATE_MATCHES]:
      'app.common.validations.setting.template.overlapping',
    [CommonErrorCodes.ERR_TOO_MANY_S2S_FLOW_TEMPLATE_MATCHES]:
      'app.common.validations.s2s_flow.template.overlapping',
    [CommonErrorCodes.ERR_DUPLICATE_S2S_FLOW]:
      'app.common.validations.s2s_flow.duplicate',
    [CommonErrorCodes.ERR_CRITERION_IN_USE]:
      'app.common.validations.criteria.used_criterion',
    [CommonErrorCodes.ERR_CRITERIA_VALUES_IN_USE]:
      'app.common.validations.criteria.used_values',
    [CommonErrorCodes.ERR_CRITERIA_VISIBILITY_NOT_ALLOWED]:
      'app.common.validations.criteria.visibility_not_allowed',
    [CommonErrorCodes.ERR_INVALID_CRITERIA_VISIBILITY_RULE]:
      'app.common.validations.criteria.invalid_visibility_rule',
    [CommonErrorCodes.ERR_INVALID_AUTOMAKER_GROUP]:
      'app.common.validations.criteria.invalid_automaker_group',
    [CommonErrorCodes.ERR_AUTOMAKER_GROUP_USED_IN_CRITERIA_VISIBILITY]:
      'app.common.validations.criteria.automaker_group_used_in_visibility',
    [CommonErrorCodes.USER_NOT_FOUND]: 'app.user.errors.not_found',
    [CommonErrorCodes.NOT_FOUND]: 'app.common.errors.resource_not_found',
    [CommonErrorCodes.ERR_FEATURE_GROUPS_USAGE]:
      'app.general_settings.errors.groups_in_use',
    [CommonErrorCodes.ERR_DESTOCKING_REQUIRES_REDISTRIBUTION]:
      'app.general_settings.errors.destocking_requires_redistribution',
    [CommonErrorCodes.ERR_DESTOCKING_MODULE_DISABLED]:
      'app.setting_template.errors.destocking_module_disabled',
    [CommonErrorCodes.ERR_INTERNAL_STOCK_LOCKED_BY_ACTIVE_DESTOCKING]:
      'app.entity.form.my_stocks.locked_by_active_destocking',
    [CommonErrorCodes.ERR_RESOURCE_VALIDATION]:
      'app.common.assert.err_resource_validation',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_FALSE]:
      'app.common.assert.err_value_should_be_false',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_TRUE]:
      'app.common.assert.err_value_should_be_true',
    [CommonErrorCodes.ERR_ORDER_VALIDATION_REQUIRES_AUTO_VALIDATION]:
      'app.redistribution.business_rules.errors.order_validation_requires_auto_validation',
    [CommonErrorCodes.ERR_DMS_SENDING_REQUIRES_DMS_ENABLED]:
      'app.redistribution.business_rules.errors.dms_sending_requires_dms_enabled',
    [CommonErrorCodes.ERR_DMS_SENDING_MODES_EXCLUSIVE]:
      'app.redistribution.business_rules.errors.dms_sending_modes_exclusive',
    [CommonErrorCodes.ERR_STORE_DELIVERY_REQUIRES_DIRECT_DELIVERY]:
      'app.redistribution.business_rules.errors.store_delivery_requires_direct_delivery',
    [CommonErrorCodes.ERR_INTERMEDIATE_STATUS_NOT_ALLOWED]:
      'app.redistribution.business_rules.errors.intermediate_status_not_allowed',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_OF_TYPE]:
      'app.common.assert.err_value_should_be_of_type',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_BLANK]:
      'app.common.assert.err_value_should_be_blank',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_VALID_CHOICE]:
      'app.common.assert.err_value_should_be_valid_choice',
    [CommonErrorCodes.ERR_MUST_SELECT_AT_LEAST_N_CHOICES]:
      'app.common.assert.err_must_select_at_least_n_choices',
    [CommonErrorCodes.ERR_MUST_SELECT_AT_MOST_N_CHOICES]:
      'app.common.assert.err_must_select_at_most_n_choices',
    [CommonErrorCodes.ERR_ONE_OR_MORE_INVALID_VALUES]:
      'app.common.assert.err_one_or_more_invalid_values',
    [CommonErrorCodes.ERR_FILE_NOT_FOUND]:
      'app.common.assert.err_file_not_found',
    [CommonErrorCodes.ERR_FILE_NOT_READABLE]:
      'app.common.assert.err_file_not_readable',
    [CommonErrorCodes.ERR_FILE_TOO_LARGE]:
      'app.common.assert.err_file_too_large',
    [CommonErrorCodes.ERR_MIME_TYPE_INVALID]:
      'app.common.assert.err_mime_type_invalid',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_LESS_THAN_OR_EQUAL_TO]:
      'app.common.assert.err_value_should_be_less_than_or_equal_to',
    [CommonErrorCodes.ERR_VALUE_TOO_LONG]:
      'app.common.assert.err_value_too_long',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_GREATER_THAN_OR_EQUAL_TO]:
      'app.common.assert.err_value_should_be_greater_than_or_equal_to',
    [CommonErrorCodes.ERR_VALUE_TOO_SHORT]:
      'app.common.assert.err_value_too_short',
    [CommonErrorCodes.ERR_VALUE_SHOULD_NOT_BE_NULL]:
      'app.common.assert.err_value_should_not_be_null',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_NULL]:
      'app.common.assert.err_value_should_be_null',
    [CommonErrorCodes.ERR_VALUE_NOT_VALID]:
      'app.common.assert.err_value_not_valid',
    [CommonErrorCodes.ERR_TWO_VALUES_SHOULD_BE_EQUAL]:
      'app.common.assert.err_two_values_should_be_equal',
    [CommonErrorCodes.ERR_FILE_COULD_NOT_BE_UPLOADED]:
      'app.common.assert.err_file_could_not_be_uploaded',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_A_VALID_NUMBER]:
      'app.common.assert.err_value_should_be_a_valid_number',
    [CommonErrorCodes.ERR_RESOURCE_MUST_BE_UNIQUE]:
      'app.common.assert.err_resource_must_be_unique',
    [CommonErrorCodes.ERR_IMAGE_SIZE_COULD_NOT_BE_DETECTED]:
      'app.common.assert.err_image_size_could_not_be_detected',
    [CommonErrorCodes.ERR_IMAGE_WIDTH_TOO_BIG]:
      'app.common.assert.err_image_width_too_big',
    [CommonErrorCodes.ERR_IMAGE_WIDTH_TOO_SMALL]:
      'app.common.assert.err_image_width_too_small',
    [CommonErrorCodes.ERR_IMAGE_HEIGHT_TOO_BIG]:
      'app.common.assert.err_image_height_too_big',
    [CommonErrorCodes.ERR_IMAGE_HEIGHT_TOO_SMALL]:
      'app.common.assert.err_image_height_too_small',
    [CommonErrorCodes.ERR_VALUE_SHOULD_HAVE_EXACTLY_N_CHARACTERS]:
      'app.common.assert.err_value_should_have_exactly_n_characters',
    [CommonErrorCodes.ERR_FILE_WAS_ONLY_PARTIALLY_UPLOADED]:
      'app.common.assert.err_file_was_only_partially_uploaded',
    [CommonErrorCodes.ERR_NO_FILE_WAS_UPLOADED]:
      'app.common.assert.err_no_file_was_uploaded',
    [CommonErrorCodes.ERR_COLLECTION_SHOULD_CONTAIN_N_OR_MORE_ELEMENTS]:
      'app.common.assert.err_collection_should_contain_n_or_more_elements',
    [CommonErrorCodes.ERR_COLLECTION_SHOULD_CONTAIN_N_OR_LESS_ELEMENTS]:
      'app.common.assert.err_collection_should_contain_n_or_less_elements',
    [CommonErrorCodes.ERR_COLLECTION_SHOULD_CONTAIN_EXACTLY_N_ELEMENTS]:
      'app.common.assert.err_collection_should_contain_exactly_n_elements',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_EQUAL_TO]:
      'app.common.assert.err_value_should_be_equal_to',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_GREATER_THAN]:
      'app.common.assert.err_value_should_be_greater_than',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_IDENTICAL_TO]:
      'app.common.assert.err_value_should_be_identical_to',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_LESS_THAN]:
      'app.common.assert.err_value_should_be_less_than',
    [CommonErrorCodes.ERR_VALUE_SHOULD_NOT_BE_EQUAL_TO]:
      'app.common.assert.err_value_should_not_be_equal_to',
    [CommonErrorCodes.ERR_VALUE_SHOULD_NOT_BE_IDENTICAL_TO]:
      'app.common.assert.err_value_should_not_be_identical_to',
    [CommonErrorCodes.ERR_IMAGE_RATIO_TOO_BIG]:
      'app.common.assert.err_image_ratio_too_big',
    [CommonErrorCodes.ERR_IMAGE_RATIO_TOO_SMALL]:
      'app.common.assert.err_image_ratio_too_small',
    [CommonErrorCodes.ERR_IMAGE_IS_SQUARE]:
      'app.common.assert.err_image_is_square',
    [CommonErrorCodes.ERR_IMAGE_IS_LANDSCAPE]:
      'app.common.assert.err_image_is_landscape',
    [CommonErrorCodes.ERR_IMAGE_IS_PORTRAIT]:
      'app.common.assert.err_image_is_portrait',
    [CommonErrorCodes.ERR_EMPTY_FILE_NOT_ALLOWED]:
      'app.common.assert.err_empty_file_not_allowed',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_A_MULTIPLE_OF]:
      'app.common.assert.err_value_should_be_a_multiple_of',
    [CommonErrorCodes.ERR_COLLECTION_SHOULD_CONTAIN_ONLY_UNIQUE_ELEMENTS]:
      'app.common.assert.err_collection_should_contain_only_unique_elements',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_EITHER_POSITIVE_OR_ZERO]:
      'app.common.assert.err_value_should_be_either_positive_or_zero',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_NEGATIVE]:
      'app.common.assert.err_value_should_be_negative',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_EITHER_NEGATIVE_OR_ZERO]:
      'app.common.assert.err_value_should_be_either_negative_or_zero',
    [CommonErrorCodes.ERR_VALUE_SHOULD_BE_BETWEEN]:
      'app.common.assert.err_value_should_be_between',
    [CommonErrorCodes.ERR_CALL_CENTER_CUSTOMER_N1_ONLY]:
      'app.common.assert.err_call_center_customer_n1_only',
    [CommonErrorCodes.ERR_JOB_PENDING]: 'app.common.assert.err_job_pending',
    [CommonErrorCodes.ERR_LANGUAGE_NOT_FOUND]: 'app.language.errors.not_found',
    [CommonErrorCodes.ERR_LANGUAGE_IN_USE_BY_USERS]:
      'app.language.errors.in_use_by_users',
    [CommonErrorCodes.ERR_LANGUAGE_IN_USE_BY_CUSTOMERS]:
      'app.language.errors.in_use_by_customers',
    [CommonErrorCodes.ERR_CONTAINER_REFERENCE_REQUIRED_AFTER_ACCEPT]:
      'app.container.errors.reference_required_after_accept',
    [CommonErrorCodes.ERR_ADVERT_SUMMARY_CANNOT_CONTAIN_INLINE_IMAGE]:
      'app.advert.form.summary.error.contains_inline_image',
    [CommonErrorCodes.ERR_ADVERT_SUMMARY_TOO_LONG]:
      'app.advert.form.summary.error.too_long',
    [CommonErrorCodes.ERR_INVALID_CUSTOMER]: 'import.customer.invalid_customer',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_EMAIL]:
      'import.user.invalid_email',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_CUSTOMER_CODES]:
      'import.user.invalid_customer_codes',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_GROUPS]:
      'import.user.invalid_groups',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_FIRST_NAME]:
      'import.user.invalid_first_name',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_LAST_NAME]:
      'import.user.invalid_last_name',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_INTERNAL_CODE]:
      'import.user.invalid_internal_code',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_LOCALE]:
      'import.user.invalid_locale',
    [CommonErrorCodes.ERR_IMPORT_USER_INVALID_ACTIVE]:
      'import.user.invalid_active',
    [CommonErrorCodes.ERR_INVALID_HEADER_LENGTH]:
      'import.users.invalid_header_length',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_DIRECT_DELIVERY_REQUIRED]:
      'import.customer.direct_delivery_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_INVALID_LOCALE]:
      'import.customer.invalid_locale',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_INVALID_USER]:
      'import.customer.invalid_user',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_NAME_REQUIRED]:
      'import.customer.name_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_DELIVERY_ADDRESS_REQUIRED]:
      'import.customer.delivery_address_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_ZIP_CODE_REQUIRED]:
      'import.customer.zip_code_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_CITY_REQUIRED]:
      'import.customer.city_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_INVALID_ACTIVE]:
      'import.customer.active_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_CRITERIA_REQUIRED]:
      'import.customer.criteria_required',
    [CommonErrorCodes.ERR_IMPORT_CUSTOMER_BILLING_CODE_REQUIRED]:
      'import.customer.billing_code_required',
    [CommonErrorCodes.ERR_CUSTOMER_CODE_ALREADY_USED]:
      'import.customer.code_already_used',
    [CommonErrorCodes.ERR_CUSTOMER_NOT_ALLOWED]:
      'import.customer.invalid_customer',
    [CommonErrorCodes.ERR_INVALID_COUNTRY]: 'import.customer.invalid_country',
    [CommonErrorCodes.ERR_INVALID_ECOTAX_ZONE]:
      'import.customer.invalid_ecotax_zone',
    [CommonErrorCodes.ERR_INVALID_VAT_ZONE]: 'import.customer.invalid_vat_zone',
    [CommonErrorCodes.ERR_INVALID_CUSTOMER_GROUP]:
      'import.customer.invalid_customer_group',
    [CommonErrorCodes.ERR_INVALID_CRITERIA]: 'import.customer.invalid_criteria',
    [CommonErrorCodes.ERR_INVALID_DIAGNOSIS_PROVIDER]:
      'import.customer.invalid_diagnosis_provider',
    [CommonErrorCodes.ERR_INVALID_QUOTE_TARGET]:
      'import.customer.invalid_quote_target',
    [CommonErrorCodes.ERR_INVALID_MANAGER]: 'import.customer.invalid_manager',
    [CommonErrorCodes.ERR_MANAGER_NOT_ALLOWED]:
      'import.customer.invalid_manager',
    [CommonErrorCodes.ERR_INCONSISTENT_CURRENCY]:
      'app.purchase.redistribution.pricing_policies.customer.currency.error',
  });

export const PriceFrameTypesLookups = createErrorLookup<string>({
  BF_EXPANDED: 'app.price_frame_types.bf_expanded',
  BF_OFFICIAL: 'app.price_frame_types.bf_official',
});

export const extractError = (
  error: IErrorApiResponse,
  lookup?: Record<string, AnyTranslationKeys>,
): AnyTranslationKeys => {
  const code = error.message;
  return (
    lookup?.[code] ??
    CommonErrorCodesLookup[code as CommonErrorCodesEnumType] ??
    (code as AnyTranslationKeys)
  );
};
